import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FavoriteButton from "@/components/FavoriteButton";
import { publicUrl } from "@/lib/storage";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Radio, Star, Newspaper, BarChart3, Mic } from "lucide-react";
import AccountSettings from "@/components/AccountSettings";

type Athlete = { id: string; slug: string; name: string; sport: string | null; country: string | null; image_path: string | null; tagline: string | null };
type Team = { id: string; slug: string; name: string; sport: string | null; logo_path: string | null };
type Product = { id: string; brand_name: string; product_name: string; category: string | null; price: string | null; image_path: string | null };
type Cause = { id: string; name: string; blurb: string | null; logo_path: string | null };
type AthleteUpdate = { athlete_id: string; message: string; updated_at: string };

const PODCAST_URL =
  "https://open.spotify.com/show/5CQuCxSH6XDW58k51xYCDD?si=c27414fdabbf4464&nd=1&dlsi=7829d14c4e204579";

const fanFun = [
  { to: "/media-hub", label: "Media Hub", desc: "Video and photo collections", Icon: Radio, external: false },
  { to: "/spotlight", label: "Spotlight", desc: "Featured stories and posts", Icon: Star, external: false },
  { to: "/news", label: "News", desc: "The latest across sports", Icon: Newspaper, external: false },
  { to: "/scores", label: "Scores", desc: "Live scores and results", Icon: BarChart3, external: false },
  { to: PODCAST_URL, label: "Podcast", desc: "Listen on Spotify", Icon: Mic, external: true },
];

const EmptyState = ({ label, browseTo, browseLabel }: { label: string; browseTo: string; browseLabel: string }) => (
  <div className="rounded-2xl bg-muted/40 border border-border p-8 text-center">
    <p className="text-muted-foreground">{label}</p>
    <Link
      to={browseTo}
      className="inline-block mt-3 text-sm font-semibold uppercase tracking-wider text-primary hover:underline"
    >
      Browse {browseLabel} →
    </Link>
  </div>
);

const FanDashboard = () => {
  const { user, loading, signOut } = useAuth();
  const [displayName, setDisplayName] = useState<string>("");
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [causes, setCauses] = useState<Cause[]>([]);
  const [updates, setUpdates] = useState<AthleteUpdate[]>([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    document.title = "Your dashboard · Say It In Sports";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Your favorited athletes, teams, brands, and causes on Say It In Sports.");
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setDataLoading(true);
      const [profileRes, aFav, tFav, pFav, cFav] = await Promise.all([
        supabase.from("profiles").select("display_name").eq("id", user.id).maybeSingle(),
        supabase.from("athlete_favorites").select("athlete_id").eq("user_id", user.id),
        (supabase as any).from("team_favorites").select("team_id").eq("user_id", user.id),
        (supabase as any).from("product_favorites").select("product_id").eq("user_id", user.id),
        (supabase as any).from("cause_favorites").select("cause_id").eq("user_id", user.id),
      ]);

      const aIds = (aFav.data ?? []).map((r: any) => r.athlete_id);
      const tIds = ((tFav.data as any) ?? []).map((r: any) => r.team_id);
      const pIds = ((pFav.data as any) ?? []).map((r: any) => r.product_id);
      const cIds = ((cFav.data as any) ?? []).map((r: any) => r.cause_id);

      const [aRes, tRes, pRes, cRes, updatesRes] = await Promise.all([
        aIds.length
          ? supabase.from("athletes").select("id,slug,name,sport,country,image_path,tagline").in("id", aIds).eq("published", true)
          : Promise.resolve({ data: [] as any[] } as any),
        tIds.length
          ? supabase.from("teams").select("id,slug,name,sport,logo_path").in("id", tIds).eq("published", true)
          : Promise.resolve({ data: [] as any[] } as any),
        pIds.length
          ? supabase.from("brand_products").select("id,brand_name,product_name,category,price,image_path").in("id", pIds).eq("published", true)
          : Promise.resolve({ data: [] as any[] } as any),
        cIds.length
          ? supabase.from("causes").select("id,name,blurb,logo_path").in("id", cIds).eq("published", true)
          : Promise.resolve({ data: [] as any[] } as any),
        aIds.length
          ? supabase.from("athlete_status").select("athlete_id,message,updated_at").in("athlete_id", aIds).eq("published", true).order("updated_at", { ascending: false })
          : Promise.resolve({ data: [] as any[] } as any),
      ]);

      if (cancelled) return;
      setDisplayName((profileRes.data as any)?.display_name ?? user.email?.split("@")[0] ?? "friend");
      setAthletes(((aRes as any).data ?? []) as Athlete[]);
      setTeams(((tRes as any).data ?? []) as Team[]);
      setProducts(((pRes as any).data ?? []) as Product[]);
      setCauses(((cRes as any).data ?? []) as Cause[]);
      setUpdates(((updatesRes as any).data ?? []) as AthleteUpdate[]);
      setDataLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const onUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail as { display_name?: string } | undefined;
      if (detail?.display_name) setDisplayName(detail.display_name);
    };
    window.addEventListener("profile:updated", onUpdate);
    return () => window.removeEventListener("profile:updated", onUpdate);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>
    );
  }
  if (!user) return <Navigate to="/auth?next=/dashboard" replace />;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container py-12">
        <header className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">My Corner</span>
            <h1 className="text-4xl md:text-5xl mt-2">Welcome back, {displayName}.</h1>
            <p className="text-muted-foreground mt-2 max-w-xl">
              The athletes you follow and their latest published updates, in one place.
            </p>
          </div>
          <button
            onClick={() => signOut()}
            className="text-sm font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
          >
            Sign out
          </button>
        </header>

        <section aria-labelledby="corner-updates" className="mb-10 border-y border-border py-6">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 id="corner-updates" className="font-display text-2xl">Latest athlete updates</h2>
              <p className="mt-1 text-sm text-muted-foreground">Updates appear here in My Corner and in your notification bell.</p>
            </div>
          </div>
          {dataLoading ? (
            <p className="mt-4 text-muted-foreground">Loading…</p>
          ) : updates.length === 0 ? (
            <div className="mt-4 rounded-lg border border-border bg-muted/40 p-5">
              <p className="font-semibold">No published updates yet.</p>
              <p className="mt-1 text-sm text-muted-foreground">Follow an athlete to build My Corner. Her published updates will appear here.</p>
              <Link to="/athletes" className="mt-3 inline-block text-sm font-semibold text-primary hover:underline">Meet athletes →</Link>
            </div>
          ) : (
            <ul className="mt-4 grid gap-3 md:grid-cols-2">
              {updates.map((update) => {
                const athlete = athletes.find((item) => item.id === update.athlete_id);
                if (!athlete) return null;
                return (
                  <li key={update.athlete_id} className="rounded-lg border border-border bg-card p-4">
                    <Link to={`/athlete/${athlete.slug}`} className="font-display text-lg hover:text-primary">{athlete.name}</Link>
                    <p className="mt-2 text-sm leading-relaxed">{update.message.replace(/<[^>]*>/g, "")}</p>
                    <p className="mt-2 text-xs text-muted-foreground">{new Date(update.updated_at).toLocaleDateString()}</p>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <Tabs defaultValue="athletes" className="w-full">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="athletes">Athletes I Follow {athletes.length ? `(${athletes.length})` : ""}</TabsTrigger>
            <TabsTrigger value="teams">Teams {teams.length ? `(${teams.length})` : ""}</TabsTrigger>
            <TabsTrigger value="brands">Brands {products.length ? `(${products.length})` : ""}</TabsTrigger>
            <TabsTrigger value="causes">Causes {causes.length ? `(${causes.length})` : ""}</TabsTrigger>
          </TabsList>

          <TabsContent value="athletes">
            {dataLoading ? (
              <p className="text-muted-foreground">Loading…</p>
            ) : athletes.length === 0 ? (
              <EmptyState label="Your corner is empty. Follow an athlete to see her here." browseTo="/athletes" browseLabel="Athletes" />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {athletes.map((a) => (
                  <Link
                    key={a.id}
                    to={`/athlete/${a.slug}`}
                    className="group relative rounded-2xl overflow-hidden bg-card shadow-card hover:shadow-bold transition-all hover:-translate-y-1 block"
                  >
                    <div className="aspect-[3/4] overflow-hidden bg-muted">
                      {a.image_path && (
                        <img src={publicUrl(a.image_path)!} alt={a.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                      )}
                    </div>
                    <div className="absolute top-2 right-2">
                      <FavoriteButton kind="athlete" id={a.id} size="sm" />
                    </div>
                    <div className="p-3">
                      <h3 className="font-display text-base truncate">{a.name}</h3>
                      {a.tagline && <p className="serif text-sm text-primary mt-0.5 truncate">{a.tagline}</p>}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="teams">
            {dataLoading ? (
              <p className="text-muted-foreground">Loading…</p>
            ) : teams.length === 0 ? (
              <EmptyState label="You haven't favorited any teams yet." browseTo="/teams" browseLabel="Teams" />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {teams.map((t) => (
                  <Link
                    key={t.id}
                    to={`/team/${t.slug}`}
                    className="group relative rounded-2xl bg-card shadow-card hover:shadow-bold transition-all hover:-translate-y-1 p-5 flex flex-col items-center text-center"
                  >
                    <div className="absolute top-2 right-2">
                      <FavoriteButton kind="team" id={t.id} size="sm" />
                    </div>
                    <div className="w-24 h-24 rounded-full bg-muted overflow-hidden flex items-center justify-center mb-3">
                      {t.logo_path ? (
                        <img src={publicUrl(t.logo_path)!} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <span className="font-display text-2xl text-muted-foreground">{t.name.charAt(0)}</span>
                      )}
                    </div>
                    <h3 className="font-display text-lg leading-tight">{t.name}</h3>
                    {t.sport && <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{t.sport}</p>}
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="brands">
            {dataLoading ? (
              <p className="text-muted-foreground">Loading…</p>
            ) : products.length === 0 ? (
              <EmptyState label="You haven't favorited any brand products yet." browseTo="/shop" browseLabel="Brands" />
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                {products.map((p) => (
                  <Link
                    key={p.id}
                    to={`/product/${p.id}`}
                    className="group relative block bg-card rounded-2xl p-5 shadow-card hover:shadow-bold transition-all"
                  >
                    <div className="absolute top-3 right-3 z-10">
                      <FavoriteButton kind="product" id={p.id} size="sm" />
                    </div>
                    <div className="aspect-square rounded-xl bg-muted mb-4 overflow-hidden flex items-center justify-center font-display text-4xl text-primary">
                      {p.image_path ? (
                        <img src={publicUrl(p.image_path)!} alt={p.product_name} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <span>{p.brand_name.charAt(0)}</span>
                      )}
                    </div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {p.brand_name}
                      {p.category ? ` · ${p.category}` : ""}
                    </p>
                    <p className="font-semibold mt-1 text-sm leading-tight">{p.product_name}</p>
                    {p.price && <p className="font-display text-lg mt-2">{p.price}</p>}
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="causes">
            {dataLoading ? (
              <p className="text-muted-foreground">Loading…</p>
            ) : causes.length === 0 ? (
              <EmptyState label="You haven't favorited any causes yet." browseTo="/causes" browseLabel="Causes" />
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {causes.map((c) => (
                  <Link
                    key={c.id}
                    to={`/cause/${c.id}`}
                    className="relative bg-card rounded-[1.5rem] p-6 shadow-card hover:shadow-bold transition-shadow flex flex-col"
                  >
                    <div className="absolute top-3 right-3">
                      <FavoriteButton kind="cause" id={c.id} size="sm" />
                    </div>
                    {c.logo_path && (
                      <div className="w-16 h-16 rounded-xl bg-muted overflow-hidden mb-4 flex items-center justify-center">
                        <img src={publicUrl(c.logo_path)!} alt={`${c.name} logo`} className="w-full h-full object-contain" />
                      </div>
                    )}
                    <h3 className="font-display text-2xl">{c.name}</h3>
                    {c.blurb && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-3">
                        {c.blurb.replace(/<[^>]+>/g, "")}
                      </p>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <section className="mt-16">
          <h2 className="text-2xl md:text-3xl font-display mb-6">Account settings</h2>
          <AccountSettings />
        </section>

        <section className="mt-16">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="text-2xl md:text-3xl font-display">Explore Fan Fun</h2>
            <span className="text-xs uppercase tracking-wider text-muted-foreground">More coming soon</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {fanFun.map(({ to, label, desc, Icon, external }) => {
              const inner = (
                <>
                  <Icon className="w-6 h-6 text-primary mb-3" />
                  <div className="font-display text-lg">{label}</div>
                  <p className="text-xs text-muted-foreground mt-1">{desc}</p>
                </>
              );
              const className =
                "group block rounded-2xl bg-card p-5 shadow-card hover:shadow-bold hover:-translate-y-0.5 transition-all";
              return external ? (
                <a key={label} href={to} target="_blank" rel="noopener noreferrer" className={className}>
                  {inner}
                </a>
              ) : (
                <Link key={label} to={to} className={className}>
                  {inner}
                </Link>
              );
            })}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default FanDashboard;
